import {
  TwirpContext,
  TwirpServer,
  RouterEvents,
  TwirpError,
  TwirpErrorCode,
  Interceptor,
  TwirpContentType,
  chainInterceptors,
} from "twirp-ts";
import {
  LoginRequest,
  LoginResponse,
  IntakeRequest,
  VerificationRequest,
  PasswordResetRequest,
  InviteTesterRequest,
  CompleteUserRegistrationRequest,
  TOTPRegistrationRequest,
  TOTPRegistrationResponse,
  TOTPRequest,
} from "./auth";
import { ErrorResponse } from "./base";

//==================================//
//          Client Code             //
//==================================//

interface Rpc {
  request(
    service: string,
    method: string,
    contentType: "application/json" | "application/protobuf",
    data: object | Uint8Array
  ): Promise<object | Uint8Array>;
}

export interface AuthServiceClient {
  Login(request: LoginRequest): Promise<LoginResponse>;
  IntakeNewAccount(request: IntakeRequest): Promise<ErrorResponse>;
  VerifyEmail(request: VerificationRequest): Promise<ErrorResponse>;
  ResendVerification(request: VerificationRequest): Promise<ErrorResponse>;
  StartPasswordReset(request: PasswordResetRequest): Promise<ErrorResponse>;
  CompletePasswordReset(request: PasswordResetRequest): Promise<ErrorResponse>;
  InviteTester(request: InviteTesterRequest): Promise<ErrorResponse>;
  CompleteUserRegistration(
    request: CompleteUserRegistrationRequest
  ): Promise<ErrorResponse>;
  StartRegistrationTOTP(
    request: TOTPRegistrationRequest
  ): Promise<TOTPRegistrationResponse>;
  CompleteRegistrationTOTP(request: TOTPRequest): Promise<ErrorResponse>;
}

export class AuthServiceClientJSON implements AuthServiceClient {
  private readonly rpc: Rpc;
  constructor(rpc: Rpc) {
    this.rpc = rpc;
    this.Login.bind(this);
    this.IntakeNewAccount.bind(this);
    this.VerifyEmail.bind(this);
    this.ResendVerification.bind(this);
    this.StartPasswordReset.bind(this);
    this.CompletePasswordReset.bind(this);
    this.InviteTester.bind(this);
    this.CompleteUserRegistration.bind(this);
    this.StartRegistrationTOTP.bind(this);
    this.CompleteRegistrationTOTP.bind(this);
  }
  Login(request: LoginRequest): Promise<LoginResponse> {
    const data = LoginRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "Login",
      "application/json",
      data as object
    );
    return promise.then((data) => LoginResponse.fromJSON(data as any));
  }

  IntakeNewAccount(request: IntakeRequest): Promise<ErrorResponse> {
    const data = IntakeRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "IntakeNewAccount",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }

  VerifyEmail(request: VerificationRequest): Promise<ErrorResponse> {
    const data = VerificationRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "VerifyEmail",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }

  ResendVerification(request: VerificationRequest): Promise<ErrorResponse> {
    const data = VerificationRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "ResendVerification",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }

  StartPasswordReset(request: PasswordResetRequest): Promise<ErrorResponse> {
    const data = PasswordResetRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "StartPasswordReset",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }

  CompletePasswordReset(request: PasswordResetRequest): Promise<ErrorResponse> {
    const data = PasswordResetRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "CompletePasswordReset",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }

  InviteTester(request: InviteTesterRequest): Promise<ErrorResponse> {
    const data = InviteTesterRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "InviteTester",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }

  CompleteUserRegistration(
    request: CompleteUserRegistrationRequest
  ): Promise<ErrorResponse> {
    const data = CompleteUserRegistrationRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "CompleteUserRegistration",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }

  StartRegistrationTOTP(
    request: TOTPRegistrationRequest
  ): Promise<TOTPRegistrationResponse> {
    const data = TOTPRegistrationRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "StartRegistrationTOTP",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      TOTPRegistrationResponse.fromJSON(data as any)
    );
  }

  CompleteRegistrationTOTP(request: TOTPRequest): Promise<ErrorResponse> {
    const data = TOTPRequest.toJSON(request);
    const promise = this.rpc.request(
      "traverse.AuthService",
      "CompleteRegistrationTOTP",
      "application/json",
      data as object
    );
    return promise.then((data) => ErrorResponse.fromJSON(data as any));
  }
}

export class AuthServiceClientProtobuf implements AuthServiceClient {
  private readonly rpc: Rpc;
  constructor(rpc: Rpc) {
    this.rpc = rpc;
    this.Login.bind(this);
    this.IntakeNewAccount.bind(this);
    this.VerifyEmail.bind(this);
    this.ResendVerification.bind(this);
    this.StartPasswordReset.bind(this);
    this.CompletePasswordReset.bind(this);
    this.InviteTester.bind(this);
    this.CompleteUserRegistration.bind(this);
    this.StartRegistrationTOTP.bind(this);
    this.CompleteRegistrationTOTP.bind(this);
  }
  Login(request: LoginRequest): Promise<LoginResponse> {
    const data = LoginRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "Login",
      "application/protobuf",
      data
    );
    return promise.then((data) => LoginResponse.decode(data as Uint8Array));
  }

  IntakeNewAccount(request: IntakeRequest): Promise<ErrorResponse> {
    const data = IntakeRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "IntakeNewAccount",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }

  VerifyEmail(request: VerificationRequest): Promise<ErrorResponse> {
    const data = VerificationRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "VerifyEmail",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }

  ResendVerification(request: VerificationRequest): Promise<ErrorResponse> {
    const data = VerificationRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "ResendVerification",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }

  StartPasswordReset(request: PasswordResetRequest): Promise<ErrorResponse> {
    const data = PasswordResetRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "StartPasswordReset",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }

  CompletePasswordReset(request: PasswordResetRequest): Promise<ErrorResponse> {
    const data = PasswordResetRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "CompletePasswordReset",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }

  InviteTester(request: InviteTesterRequest): Promise<ErrorResponse> {
    const data = InviteTesterRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "InviteTester",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }

  CompleteUserRegistration(
    request: CompleteUserRegistrationRequest
  ): Promise<ErrorResponse> {
    const data = CompleteUserRegistrationRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "CompleteUserRegistration",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }

  StartRegistrationTOTP(
    request: TOTPRegistrationRequest
  ): Promise<TOTPRegistrationResponse> {
    const data = TOTPRegistrationRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "StartRegistrationTOTP",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      TOTPRegistrationResponse.decode(data as Uint8Array)
    );
  }

  CompleteRegistrationTOTP(request: TOTPRequest): Promise<ErrorResponse> {
    const data = TOTPRequest.encode(request).finish();
    const promise = this.rpc.request(
      "traverse.AuthService",
      "CompleteRegistrationTOTP",
      "application/protobuf",
      data
    );
    return promise.then((data) => ErrorResponse.decode(data as Uint8Array));
  }
}

//==================================//
//          Server Code             //
//==================================//

export interface AuthServiceTwirp<T extends TwirpContext = TwirpContext> {
  Login(ctx: T, request: LoginRequest): Promise<LoginResponse>;
  IntakeNewAccount(ctx: T, request: IntakeRequest): Promise<ErrorResponse>;
  VerifyEmail(ctx: T, request: VerificationRequest): Promise<ErrorResponse>;
  ResendVerification(
    ctx: T,
    request: VerificationRequest
  ): Promise<ErrorResponse>;
  StartPasswordReset(
    ctx: T,
    request: PasswordResetRequest
  ): Promise<ErrorResponse>;
  CompletePasswordReset(
    ctx: T,
    request: PasswordResetRequest
  ): Promise<ErrorResponse>;
  InviteTester(ctx: T, request: InviteTesterRequest): Promise<ErrorResponse>;
  CompleteUserRegistration(
    ctx: T,
    request: CompleteUserRegistrationRequest
  ): Promise<ErrorResponse>;
  StartRegistrationTOTP(
    ctx: T,
    request: TOTPRegistrationRequest
  ): Promise<TOTPRegistrationResponse>;
  CompleteRegistrationTOTP(
    ctx: T,
    request: TOTPRequest
  ): Promise<ErrorResponse>;
}

export enum AuthServiceMethod {
  Login = "Login",
  IntakeNewAccount = "IntakeNewAccount",
  VerifyEmail = "VerifyEmail",
  ResendVerification = "ResendVerification",
  StartPasswordReset = "StartPasswordReset",
  CompletePasswordReset = "CompletePasswordReset",
  InviteTester = "InviteTester",
  CompleteUserRegistration = "CompleteUserRegistration",
  StartRegistrationTOTP = "StartRegistrationTOTP",
  CompleteRegistrationTOTP = "CompleteRegistrationTOTP",
}

export const AuthServiceMethodList = [
  AuthServiceMethod.Login,
  AuthServiceMethod.IntakeNewAccount,
  AuthServiceMethod.VerifyEmail,
  AuthServiceMethod.ResendVerification,
  AuthServiceMethod.StartPasswordReset,
  AuthServiceMethod.CompletePasswordReset,
  AuthServiceMethod.InviteTester,
  AuthServiceMethod.CompleteUserRegistration,
  AuthServiceMethod.StartRegistrationTOTP,
  AuthServiceMethod.CompleteRegistrationTOTP,
];

export function createAuthServiceServer<T extends TwirpContext = TwirpContext>(
  service: AuthServiceTwirp<T>
) {
  return new TwirpServer<AuthServiceTwirp, T>({
    service,
    packageName: "traverse",
    serviceName: "AuthService",
    methodList: AuthServiceMethodList,
    matchRoute: matchAuthServiceRoute,
  });
}

function matchAuthServiceRoute<T extends TwirpContext = TwirpContext>(
  method: string,
  events: RouterEvents<T>
) {
  switch (method) {
    case "Login":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, LoginRequest, LoginResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "Login" };
        await events.onMatch(ctx);
        return handleLoginRequest(ctx, service, data, interceptors);
      };
    case "IntakeNewAccount":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, IntakeRequest, ErrorResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "IntakeNewAccount" };
        await events.onMatch(ctx);
        return handleIntakeNewAccountRequest(ctx, service, data, interceptors);
      };
    case "VerifyEmail":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "VerifyEmail" };
        await events.onMatch(ctx);
        return handleVerifyEmailRequest(ctx, service, data, interceptors);
      };
    case "ResendVerification":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "ResendVerification" };
        await events.onMatch(ctx);
        return handleResendVerificationRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "StartPasswordReset":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "StartPasswordReset" };
        await events.onMatch(ctx);
        return handleStartPasswordResetRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "CompletePasswordReset":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "CompletePasswordReset" };
        await events.onMatch(ctx);
        return handleCompletePasswordResetRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "InviteTester":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, InviteTesterRequest, ErrorResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "InviteTester" };
        await events.onMatch(ctx);
        return handleInviteTesterRequest(ctx, service, data, interceptors);
      };
    case "CompleteUserRegistration":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<
          T,
          CompleteUserRegistrationRequest,
          ErrorResponse
        >[]
      ) => {
        ctx = { ...ctx, methodName: "CompleteUserRegistration" };
        await events.onMatch(ctx);
        return handleCompleteUserRegistrationRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "StartRegistrationTOTP":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<
          T,
          TOTPRegistrationRequest,
          TOTPRegistrationResponse
        >[]
      ) => {
        ctx = { ...ctx, methodName: "StartRegistrationTOTP" };
        await events.onMatch(ctx);
        return handleStartRegistrationTOTPRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "CompleteRegistrationTOTP":
      return async (
        ctx: T,
        service: AuthServiceTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, TOTPRequest, ErrorResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "CompleteRegistrationTOTP" };
        await events.onMatch(ctx);
        return handleCompleteRegistrationTOTPRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    default:
      events.onNotFound();
      const msg = `no handler found`;
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleLoginRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, LoginRequest, LoginResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleLoginJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleLoginProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleIntakeNewAccountRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, IntakeRequest, ErrorResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleIntakeNewAccountJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleIntakeNewAccountProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleVerifyEmailRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleVerifyEmailJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleVerifyEmailProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleResendVerificationRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleResendVerificationJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleResendVerificationProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleStartPasswordResetRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleStartPasswordResetJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleStartPasswordResetProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleCompletePasswordResetRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleCompletePasswordResetJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleCompletePasswordResetProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleInviteTesterRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, InviteTesterRequest, ErrorResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleInviteTesterJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleInviteTesterProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleCompleteUserRegistrationRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    CompleteUserRegistrationRequest,
    ErrorResponse
  >[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleCompleteUserRegistrationJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleCompleteUserRegistrationProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleStartRegistrationTOTPRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    TOTPRegistrationRequest,
    TOTPRegistrationResponse
  >[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleStartRegistrationTOTPJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleStartRegistrationTOTPProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleCompleteRegistrationTOTPRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, TOTPRequest, ErrorResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleCompleteRegistrationTOTPJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleCompleteRegistrationTOTPProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}
async function handleLoginJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, LoginRequest, LoginResponse>[]
) {
  let request: LoginRequest;
  let response: LoginResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = LoginRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      LoginRequest,
      LoginResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Login(ctx, inputReq);
    });
  } else {
    response = await service.Login(ctx, request!);
  }

  return JSON.stringify(LoginResponse.toJSON(response) as string);
}

async function handleIntakeNewAccountJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, IntakeRequest, ErrorResponse>[]
) {
  let request: IntakeRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = IntakeRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      IntakeRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.IntakeNewAccount(ctx, inputReq);
    });
  } else {
    response = await service.IntakeNewAccount(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}

async function handleVerifyEmailJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
) {
  let request: VerificationRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = VerificationRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      VerificationRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.VerifyEmail(ctx, inputReq);
    });
  } else {
    response = await service.VerifyEmail(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}

async function handleResendVerificationJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
) {
  let request: VerificationRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = VerificationRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      VerificationRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.ResendVerification(ctx, inputReq);
    });
  } else {
    response = await service.ResendVerification(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}

async function handleStartPasswordResetJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
) {
  let request: PasswordResetRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = PasswordResetRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      PasswordResetRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.StartPasswordReset(ctx, inputReq);
    });
  } else {
    response = await service.StartPasswordReset(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}

async function handleCompletePasswordResetJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
) {
  let request: PasswordResetRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = PasswordResetRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      PasswordResetRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.CompletePasswordReset(ctx, inputReq);
    });
  } else {
    response = await service.CompletePasswordReset(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}

async function handleInviteTesterJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, InviteTesterRequest, ErrorResponse>[]
) {
  let request: InviteTesterRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = InviteTesterRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      InviteTesterRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.InviteTester(ctx, inputReq);
    });
  } else {
    response = await service.InviteTester(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}

async function handleCompleteUserRegistrationJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    CompleteUserRegistrationRequest,
    ErrorResponse
  >[]
) {
  let request: CompleteUserRegistrationRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = CompleteUserRegistrationRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      CompleteUserRegistrationRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.CompleteUserRegistration(ctx, inputReq);
    });
  } else {
    response = await service.CompleteUserRegistration(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}

async function handleStartRegistrationTOTPJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    TOTPRegistrationRequest,
    TOTPRegistrationResponse
  >[]
) {
  let request: TOTPRegistrationRequest;
  let response: TOTPRegistrationResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = TOTPRegistrationRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      TOTPRegistrationRequest,
      TOTPRegistrationResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.StartRegistrationTOTP(ctx, inputReq);
    });
  } else {
    response = await service.StartRegistrationTOTP(ctx, request!);
  }

  return JSON.stringify(TOTPRegistrationResponse.toJSON(response) as string);
}

async function handleCompleteRegistrationTOTPJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, TOTPRequest, ErrorResponse>[]
) {
  let request: TOTPRequest;
  let response: ErrorResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = TOTPRequest.fromJSON(body);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      TOTPRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.CompleteRegistrationTOTP(ctx, inputReq);
    });
  } else {
    response = await service.CompleteRegistrationTOTP(ctx, request!);
  }

  return JSON.stringify(ErrorResponse.toJSON(response) as string);
}
async function handleLoginProtobuf<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, LoginRequest, LoginResponse>[]
) {
  let request: LoginRequest;
  let response: LoginResponse;

  try {
    request = LoginRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      LoginRequest,
      LoginResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Login(ctx, inputReq);
    });
  } else {
    response = await service.Login(ctx, request!);
  }

  return Buffer.from(LoginResponse.encode(response).finish());
}

async function handleIntakeNewAccountProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, IntakeRequest, ErrorResponse>[]
) {
  let request: IntakeRequest;
  let response: ErrorResponse;

  try {
    request = IntakeRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      IntakeRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.IntakeNewAccount(ctx, inputReq);
    });
  } else {
    response = await service.IntakeNewAccount(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}

async function handleVerifyEmailProtobuf<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
) {
  let request: VerificationRequest;
  let response: ErrorResponse;

  try {
    request = VerificationRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      VerificationRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.VerifyEmail(ctx, inputReq);
    });
  } else {
    response = await service.VerifyEmail(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}

async function handleResendVerificationProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, VerificationRequest, ErrorResponse>[]
) {
  let request: VerificationRequest;
  let response: ErrorResponse;

  try {
    request = VerificationRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      VerificationRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.ResendVerification(ctx, inputReq);
    });
  } else {
    response = await service.ResendVerification(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}

async function handleStartPasswordResetProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
) {
  let request: PasswordResetRequest;
  let response: ErrorResponse;

  try {
    request = PasswordResetRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      PasswordResetRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.StartPasswordReset(ctx, inputReq);
    });
  } else {
    response = await service.StartPasswordReset(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}

async function handleCompletePasswordResetProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, PasswordResetRequest, ErrorResponse>[]
) {
  let request: PasswordResetRequest;
  let response: ErrorResponse;

  try {
    request = PasswordResetRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      PasswordResetRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.CompletePasswordReset(ctx, inputReq);
    });
  } else {
    response = await service.CompletePasswordReset(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}

async function handleInviteTesterProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, InviteTesterRequest, ErrorResponse>[]
) {
  let request: InviteTesterRequest;
  let response: ErrorResponse;

  try {
    request = InviteTesterRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      InviteTesterRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.InviteTester(ctx, inputReq);
    });
  } else {
    response = await service.InviteTester(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}

async function handleCompleteUserRegistrationProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    CompleteUserRegistrationRequest,
    ErrorResponse
  >[]
) {
  let request: CompleteUserRegistrationRequest;
  let response: ErrorResponse;

  try {
    request = CompleteUserRegistrationRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      CompleteUserRegistrationRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.CompleteUserRegistration(ctx, inputReq);
    });
  } else {
    response = await service.CompleteUserRegistration(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}

async function handleStartRegistrationTOTPProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    TOTPRegistrationRequest,
    TOTPRegistrationResponse
  >[]
) {
  let request: TOTPRegistrationRequest;
  let response: TOTPRegistrationResponse;

  try {
    request = TOTPRegistrationRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      TOTPRegistrationRequest,
      TOTPRegistrationResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.StartRegistrationTOTP(ctx, inputReq);
    });
  } else {
    response = await service.StartRegistrationTOTP(ctx, request!);
  }

  return Buffer.from(TOTPRegistrationResponse.encode(response).finish());
}

async function handleCompleteRegistrationTOTPProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AuthServiceTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, TOTPRequest, ErrorResponse>[]
) {
  let request: TOTPRequest;
  let response: ErrorResponse;

  try {
    request = TOTPRequest.decode(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      TOTPRequest,
      ErrorResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.CompleteRegistrationTOTP(ctx, inputReq);
    });
  } else {
    response = await service.CompleteRegistrationTOTP(ctx, request!);
  }

  return Buffer.from(ErrorResponse.encode(response).finish());
}
